"use server";

import bcrypt from "bcryptjs";
import { prisma } from "@/lib/prisma";
import {
  forgotPasswordSchema,
  loginSchema,
  registerSchema,
  resetPasswordSchema,
  twoFactorCodeSchema,
} from "@/lib/validation";
import {
  EMAIL_VERIFICATION_TTL_MS,
  PASSWORD_RESET_TTL_MS,
  generateToken,
  hashToken,
} from "@/lib/tokens";
import { sendMail } from "@/lib/mailer";
import {
  resetPasswordTemplate,
  verifyEmailTemplate,
} from "@/lib/email-templates";
import { getAppUrl } from "@/lib/app-url";
import { markReturning } from "@/lib/returning";
import { parseRememberMe } from "@/lib/auth/session";
import { redirectToEmailTwoFactorIfNeeded } from "@/lib/auth/start-email-2fa";
import {
  attachGuestConsentsToUser,
  readConsentCookiesState,
  recordUserConsent,
  setConsentCookiesOnResponse,
} from "@/lib/consent/record";
import { getRequestClientIp } from "@/lib/consent/request-meta";
import {
  parseRegistrationUtmJson,
  submitRegistrationToBitrix24,
} from "@/lib/bitrix24/registration-lead";
import {
  attachGuestAttributionToUser,
  markAttributionBitrixSent,
  recordAttributionEvent,
} from "@/lib/attribution/store";
import {
  ruPhoneToE164,
} from "@/lib/phone/ru-phone";
import {
  checkEmailTwoFactorCode,
  clearTwoFactorPendingCookie,
  consumeEmailTwoFactorCode,
  createTwoFactorSignInAssertion,
  getTwoFactorResendCooldownSec,
  issueEmailTwoFactorCode,
  TWO_FACTOR_PENDING_COOKIE,
  TWO_FACTOR_RESEND_COOLDOWN_MS,
  twoFactorCookieOptions,
  verifyTwoFactorPendingAssertion,
} from "@/lib/auth/email-2fa";
import { cookies } from "next/headers";
import { signIn } from "@/auth";
import { AuthError } from "next-auth";
import { redirect } from "next/navigation";

export type FormState = {
  status: "idle" | "success" | "error";
  message?: string;
  fieldErrors?: Record<string, string>;
  hint?: "register" | "session_expired";
  /** Оставшееся время до повторной отправки 2FA-кода, сек. */
  resendCooldownSec?: number;
};

const BCRYPT_ROUNDS = 12;

function flattenFieldErrors(
  errors: Record<string, string[] | undefined>,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, messages] of Object.entries(errors)) {
    if (messages && messages.length > 0) result[key] = messages[0];
  }
  return result;
}

async function sendVerificationEmail(userId: string, email: string) {
  const token = generateToken();
  await prisma.emailVerificationToken.create({
    data: {
      userId,
      tokenHash: hashToken(token),
      expiresAt: new Date(Date.now() + EMAIL_VERIFICATION_TTL_MS),
    },
  });
  const url = `${getAppUrl()}/verify-email?token=${token}`;
  const tpl = verifyEmailTemplate(url);
  await sendMail({ to: email, ...tpl });
}

export async function registerAction(
  _prev: FormState,
  formData: FormData,
): Promise<FormState> {
  const { guestKey, cookiesAccepted } = await readConsentCookiesState();

  const consentCookies =
    cookiesAccepted ||
    formData.get("consentCookies") === "on" ||
    formData.get("consentCookies") === "true";
  const consentPrivacy =
    formData.get("consentPrivacy") === "on" ||
    formData.get("consentPrivacy") === "true";
  const consentNewsletter =
    formData.get("consentNewsletter") === "on" ||
    formData.get("consentNewsletter") === "true";

  const parsed = registerSchema.safeParse({
    name: formData.get("name"),
    email: formData.get("email"),
    password: formData.get("password"),
    passwordConfirm: formData.get("passwordConfirm"),
    phone: formData.get("phone"),
    consentCookies,
    consentPrivacy,
    consentNewsletter,
  });

  if (!parsed.success) {
    return {
      status: "error",
      fieldErrors: flattenFieldErrors(parsed.error.flatten().fieldErrors),
    };
  }

  const phoneE164 = ruPhoneToE164(parsed.data.phone);
  if (!phoneE164) {
    return {
      status: "error",
      fieldErrors: { phone: "Введите номер полностью: +7 (XXX) XXX-XX-XX" },
    };
  }

  const { name, email, password } = parsed.data;

  const existing = await prisma.user.findUnique({ where: { email } });
  if (existing) {
    return {
      status: "error",
      fieldErrors: { email: "Пользователь с таким email уже существует" },
    };
  }

  const passwordHash = await bcrypt.hash(password, BCRYPT_ROUNDS);
  const user = await prisma.user.create({
    data: { name, email, passwordHash, phone: phoneE164 },
  });

  await recordUserConsent({
    type: "cookies",
    accepted: true,
    userId: user.id,
    guestKey,
  });
  await recordUserConsent({
    type: "privacy",
    accepted: true,
    userId: user.id,
    guestKey,
  });
  if (parsed.data.consentNewsletter) {
    await recordUserConsent({
      type: "newsletter",
      accepted: true,
      userId: user.id,
      guestKey,
    });
  }

  await attachGuestConsentsToUser(user.id, guestKey);
  await setConsentCookiesOnResponse({
    guestKey: guestKey || user.id,
    cookiesAccepted: true,
  });

  const bitrixTrace = String(formData.get("bitrixTrace") ?? "").trim() || undefined;
  const bitrixUtm = parseRegistrationUtmJson(
    String(formData.get("bitrixUtm") ?? ""),
  );
  const landingPath =
    String(formData.get("landingPath") ?? "").trim() || "/register";
  const referrer = String(formData.get("referrer") ?? "").trim() || null;
  const registrationIp = await getRequestClientIp();

  await attachGuestAttributionToUser(user.id, guestKey);
  const signup = await recordAttributionEvent({
    userId: user.id,
    guestKey,
    channel: "email",
    kind: "signup",
    landingPath,
    referrer,
    utm: bitrixUtm,
    bitrixTrace,
  });

  const sent = await submitRegistrationToBitrix24({
    name,
    email,
    phone: phoneE164,
    newsletterConsent: parsed.data.consentNewsletter,
    trace: bitrixTrace,
    utm: bitrixUtm,
    registrationIp,
    channelLabel: "Email",
  });
  if (sent) {
    await markAttributionBitrixSent(signup?.id);
  }

  await sendVerificationEmail(user.id, email);
  await markReturning();

  return {
    status: "success",
    message:
      "Аккаунт создан. Мы отправили письмо для подтверждения email — проверьте почту.",
  };
}

export async function loginAction(
  _prev: FormState,
  formData: FormData,
): Promise<FormState> {
  const parsed = loginSchema.safeParse({
    email: formData.get("email"),
    password: formData.get("password"),
  });

  if (!parsed.success) {
    return {
      status: "error",
      fieldErrors: flattenFieldErrors(parsed.error.flatten().fieldErrors),
    };
  }

  await markReturning();

  const remember = parseRememberMe(formData.get("remember"));
  const user = await prisma.user.findUnique({
    where: { email: parsed.data.email },
    select: {
      id: true,
      email: true,
      passwordHash: true,
      twoFactorEmailEnabled: true,
    },
  });

  if (!user?.passwordHash) {
    return { status: "error", message: "Неверный email или пароль" };
  }

  const valid = await bcrypt.compare(parsed.data.password, user.passwordHash);
  if (!valid) {
    return { status: "error", message: "Неверный email или пароль" };
  }

  if (user.twoFactorEmailEnabled && user.email) {
    try {
      await redirectToEmailTwoFactorIfNeeded({
        userId: user.id,
        email: user.email,
        twoFactorEmailEnabled: true,
        remember,
      });
    } catch (error) {
      // redirect() бросает NEXT_REDIRECT — остальное (SMTP и т.п.) показываем в форме
      if (
        error &&
        typeof error === "object" &&
        "digest" in error &&
        String((error as { digest: string }).digest).startsWith("NEXT_REDIRECT")
      ) {
        throw error;
      }
      console.error("[login] 2FA email failed", error);
      return {
        status: "error",
        message:
          "Не удалось отправить код 2FA на почту. Проверьте SMTP или попробуйте позже.",
      };
    }
  }

  try {
    await signIn("credentials", {
      email: parsed.data.email,
      password: parsed.data.password,
      remember: remember ? "true" : "false",
      redirectTo: "/dashboard",
    });
  } catch (error) {
    if (error instanceof AuthError) {
      return { status: "error", message: "Неверный email или пароль" };
    }
    throw error;
  }

  return { status: "success" };
}

export async function yandexSignInAction(): Promise<void> {
  await markReturning();
  await signIn("yandex", { redirectTo: "/dashboard" });
}

export async function verifyTwoFactorAction(
  _prev: FormState,
  formData: FormData,
): Promise<FormState> {
  const parsed = twoFactorCodeSchema.safeParse({
    code: formData.get("code"),
  });
  if (!parsed.success) {
    return {
      status: "error",
      fieldErrors: flattenFieldErrors(parsed.error.flatten().fieldErrors),
    };
  }

  const jar = await cookies();
  const pendingRaw = jar.get(TWO_FACTOR_PENDING_COOKIE)?.value;
  const pending = pendingRaw
    ? verifyTwoFactorPendingAssertion(pendingRaw)
    : null;

  if (!pending) {
    return {
      status: "error",
      hint: "session_expired",
      message: "Сессия подтверждения истекла. Войдите снова.",
    };
  }

  const codeStatus = await checkEmailTwoFactorCode(
    pending.uid,
    parsed.data.code,
  );
  if (codeStatus === "invalid") {
    return { status: "error", message: "Неверный или просроченный код" };
  }

  if (codeStatus === "valid") {
    const consumed = await consumeEmailTwoFactorCode(
      pending.uid,
      parsed.data.code,
    );
    if (!consumed) {
      return { status: "error", message: "Неверный или просроченный код" };
    }
  }

  const assertion = createTwoFactorSignInAssertion(pending.uid);

  try {
    await signIn("email-2fa", {
      assertion,
      remember: pending.remember ? "true" : "false",
      redirectTo: "/dashboard",
    });
  } catch (error) {
    if (
      error &&
      typeof error === "object" &&
      "digest" in error &&
      String((error as { digest: string }).digest).startsWith("NEXT_REDIRECT")
    ) {
      throw error;
    }
    if (error instanceof AuthError) {
      return { status: "error", message: "Не удалось завершить вход" };
    }
    throw error;
  }

  return { status: "success" };
}

export async function resendTwoFactorAction(): Promise<FormState> {
  const jar = await cookies();
  const pendingRaw = jar.get(TWO_FACTOR_PENDING_COOKIE)?.value;
  const pending = pendingRaw
    ? verifyTwoFactorPendingAssertion(pendingRaw)
    : null;

  if (!pending) {
    return {
      status: "error",
      hint: "session_expired",
      message: "Сессия подтверждения истекла. Войдите снова.",
    };
  }

  const user = await prisma.user.findUnique({
    where: { id: pending.uid },
    select: { email: true, twoFactorEmailEnabled: true },
  });

  if (!user?.email || !user.twoFactorEmailEnabled) {
    return { status: "error", message: "2FA недоступна для этого аккаунта" };
  }

  const cooldownSec = await getTwoFactorResendCooldownSec(pending.uid);
  if (cooldownSec > 0) {
    return { status: "idle", resendCooldownSec: cooldownSec };
  }

  try {
    await issueEmailTwoFactorCode(pending.uid, user.email);
  } catch {
    return {
      status: "error",
      message: "Не удалось отправить код. Попробуйте позже.",
    };
  }

  return {
    status: "success",
    message: "Новый код отправлен на email.",
    resendCooldownSec: Math.ceil(TWO_FACTOR_RESEND_COOLDOWN_MS / 1000),
  };
}

export async function cancelTwoFactorAction(): Promise<void> {
  const jar = await cookies();
  jar.set(TWO_FACTOR_PENDING_COOKIE, "", {
    ...twoFactorCookieOptions(0),
    maxAge: 0,
  });
  redirect("/login");
}

export async function forgotPasswordAction(
  _prev: FormState,
  formData: FormData,
): Promise<FormState> {
  const parsed = forgotPasswordSchema.safeParse({
    email: formData.get("email"),
  });

  if (!parsed.success) {
    return {
      status: "error",
      fieldErrors: flattenFieldErrors(parsed.error.flatten().fieldErrors),
    };
  }

  const { email } = parsed.data;
  const user = await prisma.user.findUnique({ where: { email } });

  if (!user) {
    return {
      status: "error",
      message: "Аккаунт с таким email не найден.",
      fieldErrors: {
        email: "Проверьте адрес или зарегистрируйтесь на сайте.",
      },
      hint: "register",
    };
  }

  const token = generateToken();
  await prisma.passwordResetToken.create({
    data: {
      userId: user.id,
      tokenHash: hashToken(token),
      expiresAt: new Date(Date.now() + PASSWORD_RESET_TTL_MS),
    },
  });
  const url = `${getAppUrl()}/reset-password?token=${token}`;
  const tpl = resetPasswordTemplate(url);

  try {
    const mail = await sendMail({ to: email, ...tpl });
    if (!mail.delivered) {
      return {
        status: "error",
        message: "Не удалось отправить письмо. Попробуйте позже.",
      };
    }
  } catch (error) {
    console.error("[forgotPassword] sendMail failed", error);
    return {
      status: "error",
      message: "Не удалось отправить письмо. Попробуйте позже.",
    };
  }

  return {
    status: "success",
    message: `Ссылка для сброса пароля отправлена на ${email}. Проверьте почту и папку «Спам».`,
  };
}

export async function verifyEmailToken(
  token: string,
): Promise<{ ok: boolean; reason?: "invalid" | "expired" | "used" }> {
  if (!token) return { ok: false, reason: "invalid" };

  const record = await prisma.emailVerificationToken.findUnique({
    where: { tokenHash: hashToken(token) },
  });

  if (!record) return { ok: false, reason: "invalid" };
  if (record.usedAt) return { ok: false, reason: "used" };
  if (record.expiresAt < new Date()) return { ok: false, reason: "expired" };

  await prisma.$transaction([
    prisma.user.update({
      where: { id: record.userId },
      data: { emailVerifiedAt: new Date() },
    }),
    prisma.emailVerificationToken.update({
      where: { id: record.id },
      data: { usedAt: new Date() },
    }),
  ]);

  return { ok: true };
}

export async function resetPasswordAction(
  _prev: FormState,
  formData: FormData,
): Promise<FormState> {
  const parsed = resetPasswordSchema.safeParse({
    token: formData.get("token"),
    password: formData.get("password"),
  });

  if (!parsed.success) {
    return {
      status: "error",
      fieldErrors: flattenFieldErrors(parsed.error.flatten().fieldErrors),
    };
  }

  const { token, password } = parsed.data;
  const record = await prisma.passwordResetToken.findUnique({
    where: { tokenHash: hashToken(token) },
  });

  if (!record || record.usedAt || record.expiresAt < new Date()) {
    return {
      status: "error",
      message: "Ссылка недействительна или истекла. Запросите сброс заново.",
    };
  }

  const passwordHash = await bcrypt.hash(password, BCRYPT_ROUNDS);
  await prisma.$transaction([
    prisma.user.update({
      where: { id: record.userId },
      data: { passwordHash },
    }),
    prisma.passwordResetToken.update({
      where: { id: record.id },
      data: { usedAt: new Date() },
    }),
  ]);

  return {
    status: "success",
    message: "Пароль обновлён. Теперь можно войти с новым паролем.",
  };
}
